import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Typography } from '@mui/material';
import { navigateToFieldRef } from 'app/services/loanNavigation';
import { SUPPRESSED_FIELDS_QUERY } from 'app/services/loanNavigation/loanNavigation.constants';
import AccordionCDS from 'app/shared/componentsRestyled/coreDesignSystem/AccordionCDS';
import {
  LabelSize,
  LabelVariant,
} from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';
import { TableCDS } from 'app/shared/componentsRestyled/coreDesignSystem/Table';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { COMMON_DATETIME_FORMAT } from 'constants/date';
import { FeatureFlag } from 'constants/featureFlags';

import {
  selectPrimaryBorrowerIdCommonInfo,
  selectSuppressedFieldLinks,
} from 'store/loanApplications/selectors';
import { selectExtendedUsers } from 'store/users/selectors';

import { config } from './SuppressedFields.config';
import {
  accordionCustomStyles,
  getHighlightedRowStyles,
  tableCustomStyles,
} from './SuppressedFields.styles';
import { resolveFieldLinkLabel } from './SuppressedFields.utils';

const UNKNOWN_USER_LABEL = 'Unknown user';
const UNKNOWN_DATE_LABEL = '—';
const ROW_HIGHLIGHT_DURATION_MS = 2000;

/**
 * Read-only, processor/UW-facing "Suppressed fields" accountability section
 * (DEV-4387 / FE-6). Lists the field-linked checklist items an LO has suppressed
 * ("Not needed", FE-3) for the current checklist, with the field they are linked
 * to, the item text, and WHO suppressed it + WHEN. Clicking a row reuses Part One's
 * "Go to field" navigation. No un-suppress affordance here — that stays the LO's
 * FE-3 action on the field.
 */
const SuppressedFields = ({ checklistId, isLoading }) => {
  const { applicationId } = useParams();
  const history = useHistory();
  const { search } = useLocation();
  // "Go to field" resolves the Underwriting tab route against this toggle (LEN2-499).
  const isRestyledUnderwritingTabEnabled = useFeatureToggle(
    FeatureFlag.EnableRestyledUnderwritingTab,
  );

  const rows = useSelector(selectSuppressedFieldLinks(checklistId));
  const usersMap = useSelector(selectExtendedUsers);
  const primaryBorrowerId = useSelector(selectPrimaryBorrowerIdCommonInfo);

  // `?view=suppressed[&item=]` deep link (DEV-5146) — built by
  // generateSuppressedFieldsPath (nav badge, suppression toast). Read once per
  // search change; an unknown/stale `item` simply never matches a row.
  const { isDeepLinked, highlightedItemId } = useMemo(() => {
    const query = new URLSearchParams(search);

    return {
      isDeepLinked:
        query.get(SUPPRESSED_FIELDS_QUERY.VIEW_KEY) === SUPPRESSED_FIELDS_QUERY.VIEW_VALUE,
      highlightedItemId: query.get(SUPPRESSED_FIELDS_QUERY.ITEM_KEY),
    };
  }, [search]);

  const [isHighlightActive, setIsHighlightActive] = useState(false);
  const containerRef = useRef(null);
  // Keyed by the deep-linked item (not a boolean) so a SECOND View to a different
  // item on the already-mounted subtab re-arms the scroll+highlight, while
  // incidental re-renders of the same link stay suppressed (panel round 1).
  const handledDeepLinkRef = useRef(null);

  const rowsById = useMemo(() => Object.fromEntries(rows.map(row => [row.id, row])), [rows]);

  const data = useMemo(
    () =>
      rows.map(row => {
        const { label, context } = resolveFieldLinkLabel(row.fieldRef);

        return {
          id: row.id,
          fieldLabel: label,
          fieldContext: context,
          itemName: row.itemName,
          // FH-3004: the flat read resolves this name BE-side onto the row; the legacy (flag-OFF)
          // read carries no such field, so this falls back to the FE user-id lookup.
          actorName:
            row.overrideActorUserName ||
            usersMap?.[row.overrideActorUserId]?.name ||
            UNKNOWN_USER_LABEL,
          overrideDateLabel: row.overrideDate
            ? dayjs(row.overrideDate).format(COMMON_DATETIME_FORMAT)
            : UNKNOWN_DATE_LABEL,
        };
      }),
    [rows, usersMap],
  );

  const onRowClick = useCallback(
    rowId => {
      const row = rowsById[rowId];

      if (!row?.fieldRef) {
        return;
      }

      navigateToFieldRef({
        parameters: {
          fieldRef: row.fieldRef,
          // Per-borrower snapshot rows open THAT borrower's drawer; loan-wide
          // rows fall back to the primary borrower (DEV-5218).
          borrowerId: row.borrowerId ?? primaryBorrowerId,
        },
        context: { history, applicationId, isRestyledUnderwritingTabEnabled },
      });
    },
    [rowsById, history, applicationId, isRestyledUnderwritingTabEnabled, primaryBorrowerId],
  );

  const labelProps = useMemo(
    () => ({ size: LabelSize.S, title: `${rows.length}`, variant: LabelVariant.ORANGE }),
    [rows.length],
  );

  const mergedTableStyles = useMemo(
    () =>
      isHighlightActive && highlightedItemId
        ? { ...tableCustomStyles, ...getHighlightedRowStyles(highlightedItemId) }
        : tableCustomStyles,
    [isHighlightActive, highlightedItemId],
  );

  useEffect(() => {
    const deepLinkKey = isDeepLinked ? (highlightedItemId ?? 'section-only') : null;

    if (!deepLinkKey || isLoading || !rows.length || handledDeepLinkRef.current === deepLinkKey) {
      return undefined;
    }

    handledDeepLinkRef.current = deepLinkKey;
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (!highlightedItemId) {
      return undefined;
    }

    setIsHighlightActive(true);
    const timerId = setTimeout(() => setIsHighlightActive(false), ROW_HIGHLIGHT_DURATION_MS);

    return () => clearTimeout(timerId);
  }, [isDeepLinked, isLoading, rows.length, highlightedItemId]);

  if (!isLoading && !rows.length) {
    return null;
  }

  return (
    <Box ref={containerRef}>
      <AccordionCDS
        customStyles={accordionCustomStyles}
        defaultExpanded={isDeepLinked}
        isLoading={isLoading}
        labelProps={labelProps}
        title="Suppressed fields"
      >
        <Box>
          <Typography color="text.secondary" component="p" mb="12px" variant="regular14">
            Fields an operator marked &quot;Not needed&quot;, suppressing the linked checklist item.
          </Typography>
          <TableCDS
            withNoAvailableMessage
            config={config}
            customStyles={mergedTableStyles}
            data={data}
            isLoading={isLoading}
            noAvailableMessageText="No suppressed fields"
            pendingChangesTextSelector={null}
            onRowClick={onRowClick}
          />
        </Box>
      </AccordionCDS>
    </Box>
  );
};

SuppressedFields.propTypes = {
  checklistId: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
};

export default SuppressedFields;
